<template>
    <!-- Custom Navigation for White Hero -->
    <nav class="nav-blur shadow-sm fixed w-full z-10 top-0 bg-white/80">
        <div class="container mx-auto pl-6 pr-4 lg:px-0 lg:pr-6 py-4 flex justify-between items-center">
            <div class="flex items-center space-x-8">
                <ApplicationLogo color="gray">
                </ApplicationLogo>
                <div class="hidden md:flex space-x-8 text-sm mt-1">
                    <button @click="scrollToAnchor('examples')" class="text-gray-700 hover:text-blue-700 hover:border-b-2 hover:border-blue-700 font-medium pb-1 transition-colors duration-500">
                        Examples
                    </button>
                    <button @click="scrollToAnchor('pricing')" class="text-gray-700 hover:text-blue-700 hover:border-b-2 hover:border-blue-700 font-medium pb-1 transition-colors duration-500">
                        Pricing
                    </button>
                </div>
            </div>
            <div class="flex items-center space-x-4">
                <ShinyButton v-if="canRegister" :href="route('quiz.index')">
                    Try now
                </ShinyButton>
            </div>
        </div>
    </nav>

    <section id="hero" class="pt-24 pb-12 bg-white text-gray-900 relative">
        <!-- Subtle background pattern -->
        <div class="absolute inset-0 opacity-5">
            <div class="absolute inset-0" style="background-image: radial-gradient(circle at 1px 1px, #3b82f6 1px, transparent 0); background-size: 20px 20px;"></div>
        </div>
        
        <!-- Corporate gradient accent -->
        <div class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-400"></div>

                <div class="container mx-auto px-6 flex flex-col md:flex-row items-end lg:items-start relative">
            <!-- Left Content -->
            <div class="w-full md:w-1/2 md:pr-12 lg:pr-16 xl:pr-20 py-16 lg:py-20 relative flex flex-col justify-between lg:h-full">
                <!-- Content wrapper for top alignment on large screens -->
                <div class="lg:flex lg:flex-col lg:h-full">
                    <!-- Top content -->
                    <div class="lg:flex-1">
                        <!-- Corporate badge -->
                        <div class="inline-flex items-center px-4 py-2 bg-blue-50 border border-blue-200 rounded-full mb-8">
                            <span class="text-blue-700 text-sm font-medium">AI-Powered Professional Headshots</span>
                        </div>
                        
                        <!-- Main headline -->
                        <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-8">
                            Professional headshots
                            <span class="block text-blue-600">that make you stand out</span>
                        </h1>
                        
                        <!-- Subtitle -->
                        <p class="text-lg md:text-xl text-gray-600 leading-relaxed mb-12 max-w-xl">
                            Transform your casual photos into polished professional portraits. Our AI creates stunning business headshots in under an hour - perfect for your career advancement.
                        </p>
                    </div>
                    
                    <!-- CTA Section - aligned to bottom -->
                    <div class="lg:mt-auto">
                        <ShinyButton v-if="canRegister" :href="route('quiz.index')" class="w-full text-2xl py-8 px-8 font-bold">
                            Get Your Professional Headshots
                            <svg class="w-6 h-6 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                            </svg>
                        </ShinyButton>
                    </div>
                </div>
            </div>

            <!-- Right Image Section -->
            <div class="w-full md:w-1/2 mt-8 md:mt-0 py-8 md:py-16 relative">
                <div class="relative">
                    <!-- Main large image -->
                    <div class="relative rounded-2xl overflow-hidden shadow-2xl">
                        <img src="/img/output_1.jpg" alt="Professional headshot example" 
                             class="w-full h-[500px] md:h-[600px] object-cover object-top">
                        <div class="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                        
                        <!-- Label overlay for main image -->
                        <div class="absolute bottom-4 right-4 bg-gray-800/30 backdrop-blur-sm rounded-lg px-3 py-0">
                            <span class="text-gray-300 text-xs font-medium">AI generated headshot</span>
                        </div>
                    </div>
                    
                    <!-- Overlay small image -->
                    <div class="absolute bottom-4 left-4">
                        <div class="w-24 h-24 md:w-32 md:h-32 rounded-xl overflow-hidden shadow-lg border-4 border-white relative">
                            <img src="/img/input_selfie_1.jpg" alt="Before/after comparison" 
                                 class="w-full h-full object-cover">
                            
                            <!-- Label overlay for small image -->
                            <div class="absolute bottom-0 left-0 right-0 bg-gray-800/30 backdrop-blur-sm rounded px-2 py-0">
                                <span class="text-gray-300 text-xs font-medium">Selfie</span>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Floating stats card -->
                    <div class="absolute top-4 right-4 bg-white/50 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-gray-200/50">
                        <div class="text-center">
                            <div class="text-2xl font-bold text-blue-800"><60 min</div>
                            <div class="text-sm text-gray-800">Processing time</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import ShinyButton from '@/Components/ShinyButton.vue';
import ApplicationLogo from '@/Components/ApplicationLogo.vue';

defineProps({
    canRegister: {
        type: Boolean,
        default: false
    }
});

const scrollToAnchor = (anchorId) => {
    const element = document.getElementById(anchorId);
    if (element) {
        element.scrollIntoView({
            behavior: 'smooth'
        });
    }
};
</script>

<style scoped>
.gradient-text {
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.nav-blur {
    backdrop-filter: blur(10px);
}
</style>
